import '../styles/globals.css'
import Head from 'next/head'
import App from 'next/app'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import Layout from '../components/Layout'
import CustomCursor from '../components/CustomCursor'
import { AuthProvider } from '../context/AuthContext'
import { I18nProvider } from '../context/I18nContext'
import { AdminTourProvider } from '../context/AdminTourContext'
import { useI18n } from '../context/I18nContext'
import { useRouter } from 'next/router'
import { Toaster } from 'react-hot-toast'
import {
  hasSeenStartupLoader,
  isEntrancePreviewPath,
  markStartupLoaderSeen,
} from '../lib/siteEntrance'
import { getLanguageFromCookie } from '../lib/language'
import { DEFAULT_LANGUAGE } from '../lib/i18n/translations'
import WebsiteEntrance from '../components/WebsiteEntrance'

const CookieConsentBanner = dynamic(() => import('../components/CookieConsentBanner'), {
  ssr: false,
})
const CookieConsentScripts = dynamic(() => import('../components/CookieConsentScripts'), {
  ssr: false,
})

const DEFAULT_LOADER_DURATIONS = {
  firstVisit: 0,
  returnVisit: 0,
  routeChange: 0,
}

function StartupLoaderGate({
  enabled,
  durations = DEFAULT_LOADER_DURATIONS,
  previewMode = false,
  onVisibilityChange,
}) {
  const { t } = useI18n()
  const router = useRouter()
  const [showLoader, setShowLoader] = useState(false)
  const [showEntrance, setShowEntrance] = useState(false)
  const [previewDismissed, setPreviewDismissed] = useState(false)
  const hideTimerRef = useRef(null)
  const overlapTimerRef = useRef(null)
  const loaderStartRef = useRef(0)
  const entrancePreviewQuery = router.query?.entrancePreview
  const queryPreviewMode = Array.isArray(entrancePreviewQuery)
    ? entrancePreviewQuery.includes('1')
    : entrancePreviewQuery === '1'
  const runtimePreviewMode =
    typeof window !== 'undefined' && isEntrancePreviewPath(window.location.pathname + window.location.search + window.location.hash)
  const isPreviewActive = (previewMode || queryPreviewMode || runtimePreviewMode) && !previewDismissed
  const resolvedDurations = {
    firstVisit: Math.max(0, Number(durations?.firstVisit) || 0),
    returnVisit: Math.max(0, Number(durations?.returnVisit) || 0),
    routeChange: Math.max(0, Number(durations?.routeChange) || 0),
  }
  const ENTRANCE_OVERLAP_MS = 90

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
    if (overlapTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(overlapTimerRef.current)
      overlapTimerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(
    (minimumDuration, { revealEntrance = false } = {}) => {
      if (typeof window === 'undefined') return
      clearHideTimer()

      if (minimumDuration <= 0 && revealEntrance) {
        setShowLoader(false)
        setShowEntrance(true)
        return
      }

      setShowLoader(true)
      setShowEntrance(false)

      hideTimerRef.current = window.setTimeout(() => {
        if (revealEntrance) {
          setShowEntrance(true)
          overlapTimerRef.current = window.setTimeout(() => {
            setShowLoader(false)
            overlapTimerRef.current = null
          }, ENTRANCE_OVERLAP_MS)
        } else {
          setShowLoader(false)
        }
        hideTimerRef.current = null
      }, minimumDuration)
    },
    [clearHideTimer],
  )

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setShowLoader(false)
      setShowEntrance(false)
      return undefined
    }

    if (previewDismissed) {
      clearHideTimer()
      setShowLoader(false)
      setShowEntrance(false)
      return undefined
    }

    if (isPreviewActive) {
      clearHideTimer()
      setShowLoader(false)
      setShowEntrance(true)
      return undefined
    }

    const shouldShowEntrance = !hasSeenStartupLoader()
    const minimumDuration = shouldShowEntrance ? resolvedDurations.firstVisit : resolvedDurations.returnVisit

    if (!shouldShowEntrance && minimumDuration <= 0) {
      setShowLoader(false)
      setShowEntrance(false)
      return () => {
        clearHideTimer()
      }
    }

    loaderStartRef.current = Date.now()
    setShowLoader(false)
    scheduleHide(minimumDuration, { revealEntrance: shouldShowEntrance })

    return () => {
      clearHideTimer()
    }
  }, [
    clearHideTimer,
    enabled,
    isPreviewActive,
    previewDismissed,
    resolvedDurations.firstVisit,
    resolvedDurations.returnVisit,
    scheduleHide,
  ])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const { body } = document
    const shouldBlockShell = enabled && (showLoader || showEntrance)

    body.classList.toggle('startup-loading', shouldBlockShell)
  }, [enabled, showEntrance, showLoader])

  useEffect(() => {
    if (!onVisibilityChange) return

    onVisibilityChange(enabled && (showLoader || showEntrance))
  }, [enabled, onVisibilityChange, showEntrance, showLoader])

  useEffect(() => {
    if (!enabled || isPreviewActive) return undefined

    const isPublicUrl = (url) => {
      const safeUrl = String(url || '')
      return !safeUrl.startsWith('/admin') && !safeUrl.startsWith('/maintenance')
    }

    const handleRouteStart = (url) => {
      if (resolvedDurations.routeChange <= 0) return
      if (!isPublicUrl(url)) return
      clearHideTimer()
      loaderStartRef.current = Date.now()
      setShowLoader(true)
    }

    const handleRouteDone = (url) => {
      if (resolvedDurations.routeChange <= 0) return
      if (!isPublicUrl(url || router.asPath)) return
      scheduleHide(resolvedDurations.routeChange)
    }

    router.events.on('routeChangeStart', handleRouteStart)
    router.events.on('routeChangeComplete', handleRouteDone)
    router.events.on('routeChangeError', handleRouteDone)

    return () => {
      router.events.off('routeChangeStart', handleRouteStart)
      router.events.off('routeChangeComplete', handleRouteDone)
      router.events.off('routeChangeError', handleRouteDone)
    }
  }, [clearHideTimer, enabled, isPreviewActive, resolvedDurations.routeChange, router.asPath, router.events, scheduleHide])

  function dismissPreview() {
    markStartupLoaderSeen()
    setPreviewDismissed(true)
    setShowLoader(false)
    setShowEntrance(false)
    clearHideTimer()
    if (isPreviewActive || isEntrancePreviewPath(router.asPath)) {
      router.replace('/')
    }
  }

  return (
    <>
      <div
        className="startup-overlay"
        style={
          enabled && (showLoader || showEntrance)
            ? { position: 'fixed', inset: 0, zIndex: 10000, pointerEvents: 'auto', opacity: 1 }
            : undefined
        }
      >
        {showEntrance ? (
          <WebsiteEntrance label={t('common.loading')} onDismiss={dismissPreview} />
        ) : null}
      </div>
    </>
  )
}

function AdminRouteProgress({ enabled }) {
  const router = useRouter()
  const [active, setActive] = useState(false)
  const [progress, setProgress] = useState(0)
  const progressTimerRef = useRef(null)
  const finishTimerRef = useRef(null)

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current && typeof window !== 'undefined') {
      window.clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  const clearFinishTimer = useCallback(() => {
    if (finishTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(finishTimerRef.current)
      finishTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setActive(false)
      setProgress(0)
      clearProgressTimer()
      clearFinishTimer()
      return undefined
    }

    const isAdminUrl = (url) => String(url || '').startsWith('/admin')

    const handleRouteStart = (url) => {
      if (!isAdminUrl(url)) return
      clearProgressTimer()
      clearFinishTimer()
      setActive(true)
      setProgress(16)
      progressTimerRef.current = window.setInterval(() => {
        setProgress((current) => (current >= 82 ? current : current + Math.max(3, (86 - current) * 0.18)))
      }, 120)
    }

    const handleRouteDone = () => {
      clearProgressTimer()
      clearFinishTimer()
      setProgress(100)
      finishTimerRef.current = window.setTimeout(() => {
        setActive(false)
        setProgress(0)
        finishTimerRef.current = null
      }, 180)
    }

    router.events.on('routeChangeStart', handleRouteStart)
    router.events.on('routeChangeComplete', handleRouteDone)
    router.events.on('routeChangeError', handleRouteDone)

    return () => {
      clearProgressTimer()
      clearFinishTimer()
      router.events.off('routeChangeStart', handleRouteStart)
      router.events.off('routeChangeComplete', handleRouteDone)
      router.events.off('routeChangeError', handleRouteDone)
    }
  }, [clearFinishTimer, clearProgressTimer, enabled, router.events])

  if (!enabled) {
    return null
  }

  return (
    <div
      className={`pointer-events-none fixed inset-x-0 top-0 z-[120] transition-opacity duration-150 ${
        active ? 'opacity-100' : 'opacity-0'
      }`}
      aria-hidden="true"
    >
      <div className="h-[3px] w-full bg-transparent">
        <div
          className="h-full bg-[linear-gradient(90deg,#ff7a1a_0%,#ffb067_55%,#ffe3c6_100%)] shadow-[0_0_18px_rgba(255,122,26,0.4)] transition-[width] duration-150 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="h-10 bg-[linear-gradient(180deg,rgba(255,122,26,0.12),rgba(255,122,26,0))]" />
    </div>
  )
}

function DocumentLanguageSync() {
  const { language } = useI18n()

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.lang = language === 'fr' ? 'fr' : 'en'
  }, [language])

  return null
}

export default function MyApp({ Component, pageProps }) {
  const { __initialLanguage, ...restPageProps } = pageProps || {}
  const router = useRouter()
  const isAdminRoute = router.pathname.startsWith('/admin')
  const isMaintenanceRoute = router.pathname === '/maintenance'
  const loaderDurations = DEFAULT_LOADER_DURATIONS
  const [startupOverlayActive, setStartupOverlayActive] = useState(
    !isAdminRoute && !isMaintenanceRoute,
  )
  const isEntrancePreviewRoute =
    router.query?.entrancePreview === '1' ||
    isEntrancePreviewPath(router.asPath) ||
    (typeof window !== 'undefined' &&
      isEntrancePreviewPath(window.location.pathname + window.location.search + window.location.hash))

  useEffect(() => {
    setStartupOverlayActive(!isAdminRoute && !isMaintenanceRoute)
  }, [isAdminRoute, isMaintenanceRoute, router.asPath])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const { body } = document
    const isPublicRoute = !isAdminRoute && !isMaintenanceRoute
    body.classList.toggle('public-site', isPublicRoute)

    if (!isPublicRoute) {
      return () => {
        body.classList.remove('public-site')
      }
    }

    const handleContextMenu = (event) => {
      if (!(event.target instanceof Element)) return
      if (event.target.closest('img')) {
        event.preventDefault()
      }
    }

    const handleDragStart = (event) => {
      if (!(event.target instanceof Element)) return
      if (event.target.closest('img')) {
        event.preventDefault()
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('dragstart', handleDragStart)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('dragstart', handleDragStart)
      body.classList.remove('public-site')
    }
  }, [isAdminRoute, isMaintenanceRoute])

  return (
    <I18nProvider initialLanguage={__initialLanguage || DEFAULT_LANGUAGE}>
      <AuthProvider>
        <AdminTourProvider>
          {isAdminRoute ? (
            <Head>
              <meta name="robots" content="noindex,nofollow,noarchive" />
            </Head>
          ) : null}
          <DocumentLanguageSync />
          {/* <CustomCursor /> */}
          <StartupLoaderGate
            enabled={(!isAdminRoute && !isMaintenanceRoute) || isEntrancePreviewRoute}
            durations={loaderDurations}
            previewMode={isEntrancePreviewRoute}
            onVisibilityChange={setStartupOverlayActive}
          />
          <AdminRouteProgress enabled={isAdminRoute} />
          <Toaster
            position="bottom-center"
            toastOptions={{
              duration: 2800,
              style: {
                background: 'var(--dash-surface)',
                color: 'var(--dash-text)',
                border: '1px solid var(--dash-border)',
                gap: '10px',
                padding: '0.75rem 1.5rem',
              },
            }}
          />
          <div className="startup-app-shell">
            {isAdminRoute || isMaintenanceRoute ? (
              <Component {...restPageProps} />
            ) : (
              <Layout>
                <Component {...restPageProps} />
              </Layout>
            )}
          </div>
          {!isAdminRoute && !isMaintenanceRoute ? (
            <>
              <CookieConsentScripts />
              {!startupOverlayActive ? <CookieConsentBanner /> : null}
            </>
          ) : null}
        </AdminTourProvider>
      </AuthProvider>
    </I18nProvider>
  )
}

MyApp.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext)
  const cookieHeader =
    appContext?.ctx?.req?.headers?.cookie ||
    (typeof document !== 'undefined' ? document.cookie : '')
  const initialLanguage = getLanguageFromCookie(cookieHeader) || DEFAULT_LANGUAGE

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      __initialLanguage: initialLanguage,
    },
  }
}
